import { useCallback } from "react";

const SOFT_MANDATORY_FIELDS = [
  "ugDegree",
  "ugDepartment",
  "ugPassout",
  "ugPercentage",
  "pgDegree",
  "pgDepartment",
  "pgPassout",
  "pgPercentage",
  "experience",
  "desiredlocation",
  "technicalScore",
  "communicationScore",
  "project1Score",
  "mockTest1Score",
  "gender",
  "diplomaDepartment",
  "diplomaPassout",
  "diplomaPercentage",
  "resumePath"
];

const SOFT_FIELD_LABELS = {
  ugDegree: "UG Degree",
  pgDegree: "PG Degree",
  ugPassout: "UG Passout",
  pgPassout: "PG Passout",
  ugPercentage: "UG Percentage",
  pgPercentage: "PG Percentage",
  pgDepartment: "PG Department",
  ugDepartment: "UG Department",
  experience: "Experience",
  desiredlocation: "Preferred Job Location",
  technicalScore: "Technical Score",
  communicationScore: "Communication Score",
  project1Score: "Project 1 Score",
  mockTest1Score: "Mock Test 1 Score",
  gender: "Gender",
  diplomaPercentage: "Diploma Percentage",
  diplomaPassout: "Diploma Passout",
  diplomaDepartment: "Diploma Department",
  resumePath: "Resume"
};

// FIXED — diploma group WITHOUT diplomaDegree
const ACADEMIC_GROUPS = {
  ug: ["ugDegree", "ugDepartment", "ugPassout", "ugPercentage"],
  pg: ["pgDegree", "pgDepartment", "pgPassout", "pgPercentage"],
  diploma: ["diplomaDepartment", "diplomaPassout", "diplomaPercentage"]
};

export const useSoftMandatoryWarning = (formData, studentData) => {
  const getSoftMandatoryWarnings = useCallback(() => {

    // ⭐ Unified getter → checks formData first, then backend saved data
    const getValue = (field) => {
      return (
        formData?.[field] ??
        studentData?.[field] ??
        studentData?.courseDetails?.[field] ??
        ""
      );
    };

    // ⭐ Detect active qualification group (UG / PG / Diploma)
    const getFilledGroup = () => {
      for (const [groupName, fields] of Object.entries(ACADEMIC_GROUPS)) {
        const started = fields.some((field) => {
          const value = getValue(field);
          return value !== "" && value !== null && value !== undefined && value !== 0;
        });

        if (started) return groupName;
      }
      return null;
    };

    const activeGroup = getFilledGroup();

    // ⭐ Validate all soft mandatory fields
    const emptyFields = SOFT_MANDATORY_FIELDS.filter((field) => {
      const value = getValue(field);

      if (field === "resumePath") {
        const resume =
          formData?.resumePath ||
          studentData?.resumePath ||
          studentData?.courseDetails?.resumePath;

        return !resume;
      }

      const isAcademicField = Object.values(ACADEMIC_GROUPS)
        .flat()
        .includes(field);

      // ⭐ CASE 1 — No group chosen → SKIP ALL academic fields (but continue others)
      if (!activeGroup && isAcademicField) {
        return false;
      }

      // ⭐ CASE 2 — Group chosen → validate ONLY fields in that group
      if (activeGroup && isAcademicField) {
        if (!ACADEMIC_GROUPS[activeGroup].includes(field)) return false;
      }

      // ⭐ SPECIAL CASE — EXPERIENCE
      if (field === "experience") {
        return (
          !value ||
          value.trim?.() === "" ||
          value === "Not Provided"
        );
      }

      // ⭐ Generic empty checks
      if (Array.isArray(value)) return value.length === 0;
      if (typeof value === "number") return value === 0;
      return value === "" || value === null || value === undefined;
    });

    let warnings = emptyFields.map((f) => SOFT_FIELD_LABELS[f] || f);

    // ⭐ ADD EXTRA QUALIFICATION MESSAGE
    if (!activeGroup) {
      warnings.unshift(
        "Please fill at least one education qualification (UG / PG / Diploma)"
      );
    }

    return warnings;

  }, [formData, studentData]);

  return { getSoftMandatoryWarnings };
};

